import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';

import Absence from '../../components/tables/Absence.jsx';
import PageHeader from '../../components/headers/Header';
import AbsenceDetailsModal from '../../components/DViews/AbsenceDetailsModal';
import DayDetailsModal from '../../components/DViews/CDayDetails.jsx';
import JustificationUploadModal from '../../components/DViews/JUModal.jsx';
import ExportButtons from '../../components/btns/ExportButtons';

import { getAllEmployees } from '../../services/EmployeeService';
import { getAllStagedAbsences } from '../../services/absenceAPIs';
import { connectWebSocket } from '../../api';
import '../../components/leaves/Leaves.css';

import XLSX from 'xlsx-js-style';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import marabesLogo from '../../assets/images/logos/Marabes.png';
import companyLogo from '../../assets/images/logos/Axopeople.png';

// 🔹 Map absence types to titles
const ABSENCE_TYPE_CONFIG = {
  SICKNESS: 'Confirm Sickness Absences',
  PERSONAL: 'Confirm Personal Absences',
  LATENESS: 'Confirm Lateness Records',
  DAY_OFF: 'Confirm Day Off Requests',
  DEFAULT: 'Confirm Employees Absences',
};

const getBase64FromUrl = (url) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.setAttribute('crossOrigin', 'anonymous');
    img.src = url;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => {
      console.warn("Failed to load image:", url);
      resolve(null);
    };
  });
};

const UnconfirmedAbsences = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [IsEmployee, setIsEmployee] = useState(localStorage.getItem('userRole') === 'EMPLOYEE');
  const [employees, setEmployees] = useState([]);
  const [stagedAbsences, setStagedAbsences] = useState([]);
  const [filteredAbsences, setFilteredAbsences] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedAbsence, setSelectedAbsence] = useState(null);
  const [selectedUpload, setSelectedUpload] = useState(null);
  const [showDayDetailsModal, setShowDayDetailsModal] = useState(false);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [dayAbsences, setDayAbsences] = useState([]);

  const isLargeScreen = useMediaQuery('(min-width: 1600px)');
  const absenceType = location.state?.absenceType || 'DEFAULT';
  const pageTitle = ABSENCE_TYPE_CONFIG[absenceType] || ABSENCE_TYPE_CONFIG.DEFAULT;

  const [filters, setFilters] = useState({
    type: '',
    status: 'pending',
    department: '',
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    search: '',
    includeLateness: true,
  });

  useEffect(() => {
    const employeeFilter = location.state?.employeeFilter;
    const statusFilter = location.state?.status || 'pending';
    if (employeeFilter) {
      setFilters(prev => ({
        ...prev,
        search: employeeFilter,
        status: statusFilter,
        month: null,
        year: null
      }));
    }
  }, [location.state]);

  // 🔹 Fetch all employees once
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const allEmployees = await getAllEmployees();
        setEmployees(allEmployees);
      } catch (error) {
        console.error('Error fetching employees:', error);
      }
    };
    fetchEmployees();
  }, []);

  // 🔹 Fetch absences filtered by type
  const fetchAbsences = async () => {
    try {
      const data = await getAllStagedAbsences();
      const filtered =
        absenceType === 'DEFAULT'
          ? data
          : data.filter(a => a.ty_pe === absenceType);
      setStagedAbsences(filtered);
    } catch (error) {
      console.error('Error fetching absences:', error);
    }
  };

  // 🔹 Initial fetch + WebSocket setup
  useEffect(() => {
    fetchAbsences();

    const disconnect = connectWebSocket((topic, payload) => {
      if (topic === 'absence') {
        console.log('🟢 WebSocket Absence Payload:', payload);
        const absence = payload;

        setStagedAbsences(prev => {
          const exists = prev.some(item => item.absence?.id === absence?.id);
          if (exists) {
            return prev.map(item =>
              item.absence?.id === absence?.id ? payload : item
            );
          } else {
            return [payload, ...prev];
          }
        });
      }
    });

    return () => disconnect && disconnect();
  }, [absenceType]); // refetch when type changes

  // 🔹 Re-fetch when user role changes
  useEffect(() => {
    if (IsEmployee) {
      setIsEmployee(localStorage.getItem('userRole') === 'EMPLOYEE');
    }
  }, [IsEmployee]);

  // 🔹 Helper functions for exports
  const formatHoursToDisplay = (hoursDecimal) => {
    if (!hoursDecimal && hoursDecimal !== 0) return '0h';
    const totalHours = parseFloat(hoursDecimal);
    if (isNaN(totalHours)) return 'Invalid';
    if (totalHours < 1) {
      const minutes = Math.round(totalHours * 60);
      return `${minutes} min`;
    }
    const hours = Math.floor(totalHours);
    const minutes = Math.round((totalHours - hours) * 60);
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}min`;
  };

  const formatAbsenceDate = (record) => {
    const start = record.startDate || record.date;
    const end = record.endDate;
    if (!start) return 'N/A';
    const formatDateDayMonthYear = (dateString) => {
      if (!dateString) return 'N/A';
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return 'N/A';
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    };
    const startFormatted = formatDateDayMonthYear(start);
    if (end) {
      const endFormatted = formatDateDayMonthYear(end);
      if (startFormatted !== endFormatted && endFormatted !== 'N/A') {
        return `${startFormatted} to ${endFormatted}`;
      }
    }
    return startFormatted;
  };

  const calculateDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return 1;
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 1;
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  // 🔹 Excel Export using xlsx-js-style
  const handleExportExcel = () => {
    try {
      if (filteredAbsences.length === 0) {
        alert('No data available to export');
        return;
      }

      const excelData = filteredAbsences.map(a => {
        const date = a?.date || null;
        const endDate = a?.endDate || null;
        const hours = a?.hours || 0;
        let derivedType = 'FULL_DAY';
        if (date && endDate && date !== endDate) derivedType = 'MULTIPLE_DAYS';
        else if (parseFloat(hours) > 0 && parseFloat(hours) < 8) derivedType = 'PARTIAL_DAY';

        const durationStr = derivedType === 'MULTIPLE_DAYS' && endDate
          ? `${calculateDuration(date, endDate)} days`
          : derivedType === 'FULL_DAY'
            ? '1 day'
            : derivedType === 'PARTIAL_DAY'
              ? formatHoursToDisplay(hours)
              : 'Open';

        const formatLeaveType = (type) => {
          if (!type) return '-';
          return type.toString().replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
        };

        return {
          'Employee': `${a.employee?.firstName || ''} ${a.employee?.lastName || ''}`.trim() || 'N/A',
          'Department': (typeof a.employee?.department === 'object' ? a.employee?.department?.name : a.employee?.department) || 'N/A',
          'Office': (typeof a.employee?.office === 'object' ? a.employee?.office?.name : a.employee?.office) || 'N/A',
          'Absence Type': formatLeaveType(a.ty_pe || 'Unknown'),
          'Dates': formatAbsenceDate(a),
          'Duration': durationStr,
          'Reason': a.reason || 'N/A',
          'Recorded By': `${a.employee?.firstName || ''} ${a.employee?.lastName || ''}`.trim() || 'N/A'
        };
      });

      const ws = XLSX.utils.json_to_sheet(excelData);

      const headerStyle = {
        font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11, name: 'Calibri' },
        fill: { fgColor: { rgb: '1E293B' } },
        alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
        border: {
          top: { style: 'thin', color: { rgb: '475569' } },
          bottom: { style: 'thin', color: { rgb: '475569' } },
          left: { style: 'thin', color: { rgb: '475569' } },
          right: { style: 'thin', color: { rgb: '475569' } }
        }
      };

      const dataStyle = {
        font: { name: 'Calibri', sz: 11 },
        border: {
          top: { style: 'thin', color: { rgb: 'CBD5E1' } },
          bottom: { style: 'thin', color: { rgb: 'CBD5E1' } },
          left: { style: 'thin', color: { rgb: 'CBD5E1' } },
          right: { style: 'thin', color: { rgb: 'CBD5E1' } }
        },
        alignment: { horizontal: 'center', vertical: 'middle' }
      };

      const range = XLSX.utils.decode_range(ws['!ref']);

      // Style headers
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellRef = XLSX.utils.encode_cell({ c: C, r: 0 });
        if (ws[cellRef]) ws[cellRef].s = headerStyle;
      }

      // Style data cells
      for (let R = range.s.r + 1; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cellRef = XLSX.utils.encode_cell({ c: C, r: R });
          if (ws[cellRef]) ws[cellRef].s = dataStyle;
        }
      }

      // Adjust column widths
      const colWidths = [];
      excelData.forEach(row => {
        Object.entries(row).forEach(([key, value], index) => {
          const cellValue = value?.toString() || '';
          const width = Math.min(35, Math.max(12, cellValue.length + 2, key.length + 2));
          if (!colWidths[index] || width > colWidths[index]) {
            colWidths[index] = width;
          }
        });
      });
      ws['!cols'] = colWidths.map(w => ({ wch: w }));

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Absence Requests');

      const fileName = `Absence_Requests_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error('Excel export failed:', error);
      alert('Excel export failed: ' + error.message);
    }
  };

  // 🔹 PDF Export using jsPDF + jspdf-autotable
  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      if (filteredAbsences.length === 0) {
        alert('No data available to export');
        setIsExporting(false);
        return;
      }

      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 10;
      const contentWidth = pageWidth - (margin * 2);
      let currentPage = 1;

      // Base64 Logos
      const [marabesBase64, axoBase64] = await Promise.all([
        getBase64FromUrl(marabesLogo),
        getBase64FromUrl('/cLogo.png')
      ]);

      const addPDFHeader = (pageNum) => {
        if (pageNum === 1) {
          const headerHeight = 22;
          doc.setFillColor(248, 250, 252);
          doc.rect(0, 0, pageWidth, headerHeight, 'F');

          const logoWidth = 28;
          const logoHeight = 11;
          const logoY = (headerHeight - logoHeight) / 2; // 5.5

          if (marabesBase64) {
            try { doc.addImage(marabesBase64, 'PNG', margin, logoY, logoWidth, logoHeight); } catch (e) { }
          }
          if (axoBase64) {
            try { doc.addImage(axoBase64, 'PNG', pageWidth - margin - logoWidth, logoY, logoWidth, logoHeight); } catch (e) { }
          }

          doc.setFontSize(13);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(30, 41, 59);
          doc.text('Absence Requests', pageWidth / 2, 11.5, { align: 'center' });

          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(100, 116, 139);
          doc.text(
            `Generated: ${new Date().toLocaleDateString('en-GB')}`,
            pageWidth / 2,
            17.5,
            { align: 'center' }
          );
        }
      };

      const addPDFFooter = () => {
        doc.setLineWidth(0.5);
        doc.setDrawColor(226, 232, 240);
        doc.line(margin, pageHeight - 20, pageWidth - margin, pageHeight - 20);

        doc.setFontSize(7);
        doc.setTextColor(148, 163, 184);
        doc.text('Confidential - For Internal Use Only', pageWidth / 2, pageHeight - 15, { align: 'center' });

        doc.setFontSize(7);
        doc.text(`Total Records: ${filteredAbsences.length}`, margin, pageHeight - 10, { align: 'left' });
      };

      addPDFHeader(currentPage);

      const tableRows = filteredAbsences.map(a => {
        const date = a?.date || null;
        const endDate = a?.endDate || null;
        const hours = a?.hours || 0;
        let derivedType = 'FULL_DAY';
        if (date && endDate && date !== endDate) derivedType = 'MULTIPLE_DAYS';
        else if (parseFloat(hours) > 0 && parseFloat(hours) < 8) derivedType = 'PARTIAL_DAY';

        const durationStr = derivedType === 'MULTIPLE_DAYS' && endDate
          ? `${calculateDuration(date, endDate)} days`
          : derivedType === 'FULL_DAY'
            ? '1 day'
            : derivedType === 'PARTIAL_DAY'
              ? formatHoursToDisplay(hours)
              : 'Open';

        const formatLeaveType = (type) => {
          if (!type) return '-';
          return type.toString().replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
        };

        return [
          `${a.employee?.firstName || ''} ${a.employee?.lastName || ''}`.trim() || 'N/A',
          (typeof a.employee?.department === 'object' ? a.employee?.department?.name : a.employee?.department) || 'N/A',
          (typeof a.employee?.office === 'object' ? a.employee?.office?.name : a.employee?.office) || 'N/A',
          formatLeaveType(a.ty_pe || 'Unknown'),
          formatAbsenceDate(a),
          durationStr,
          a.reason || 'N/A'
        ];
      });

      autoTable(doc, {
        startY: 27,
        head: [['Employee', 'Department', 'Office', 'Type', 'Dates', 'Duration', 'Reason']],
        body: tableRows,
        theme: 'grid',
        styles: { fontSize: 8, halign: 'center', valign: 'middle' },
        headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255], fontStyle: 'bold' },
        margin: { left: margin, right: margin },
        columnStyles: {
          0: { cellWidth: 35, fontStyle: 'bold', halign: 'left' },
          1: { cellWidth: 25 },
          2: { cellWidth: 20 },
          3: { cellWidth: 22 },
          4: { cellWidth: 30 },
          5: { cellWidth: 20 },
          6: { cellWidth: 'auto', halign: 'left' }
        },
        didDrawPage: () => {
          addPDFFooter();
        }
      });

      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(100, 116, 139);
        doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
      }

      const fileName = `Absence_Requests_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error('PDF export failed:', error);
      alert('PDF export failed: ' + error.message);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div>
      <PageHeader
        title={pageTitle}
        subtitle="Review and process unconfirmed absence records"
        actions={
          <ExportButtons
            onExportPDF={handleExportPDF}
            onExportExcel={handleExportExcel}
            isExporting={isExporting}
            disabled={filteredAbsences.length === 0}
          />
        }
      />

      <div id="absences-container">
        <Absence
          filters={filters}
          setFilters={setFilters}
          setSelectedAbsence={setSelectedAbsence}
          absences={stagedAbsences}
          setSelectedUpload={setSelectedUpload}
          unconfirmed={true}
          fetchAbsences={fetchAbsences}
          IsEmployee={IsEmployee}
          hideTypeFilter={false}
          hideExportButtons={true}
          onFilteredDataChange={setFilteredAbsences}
        />
      </div>

      {/* 🔹 Absence Details Modal */}
      {selectedAbsence && (
        <AbsenceDetailsModal
          selectedAbsence={selectedAbsence}
          setSelectedAbsence={setSelectedAbsence}
          fetchAbsences={fetchAbsences}
          IsEmployee={IsEmployee}
        />
      )}

      {/* 🔹 Justification Upload Modal */}
      {selectedUpload && (
        <JustificationUploadModal
          selectedUpload={selectedUpload}
          setSelectedUpload={setSelectedUpload}
          fetchAbsences={fetchAbsences}
        />
      )}

      {/* 🔹 Day Details Modal */}
      <DayDetailsModal
        open={showDayDetailsModal}
        onClose={() => setShowDayDetailsModal(false)}
        selectedDate={selectedCalendarDate}
        dayAbsences={dayAbsences}
        employees={employees}
      />
    </div>
  );
};

export default UnconfirmedAbsences;
